import React from 'react';

interface PersonIconProps {
  className?: string;
}

/**
 * Male person icon — blue colored.
 * A standalone SVG that can be placed next to other icons for doubles.
 */
export const MaleIcon: React.FC<PersonIconProps> = ({ className = 'w-6 h-6' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    {/* head */}
    <circle cx={12} cy={7} r={4} fill="#2da368" />
    {/* body — broad shoulders */}
    <path
      d="M4 22 C4 16, 7 13, 12 13 C17 13, 20 16, 20 22"
      fill="#2da368"
    />
  </svg>
);

/**
 * Female person icon — pink colored.
 * A standalone SVG that can be placed next to other icons for doubles.
 */
export const FemaleIcon: React.FC<PersonIconProps> = ({ className = 'w-6 h-6' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    {/* head */}
    <circle cx={12} cy={7} r={4} fill="#7C3AED" />
    {/* body — narrower shoulders with A-line flare */}
    <path
      d="M5 22 C5 16, 8 13, 12 13 C16 13, 19 16, 19 22"
      fill="#7C3AED"
    />
  </svg>
);

interface MatchTypeIconProps {
  matchType: string;
  className?: string;
  iconSize?: string;
}

/**
 * Composes MaleIcon and FemaleIcon based on match type.
 *
 * - mens_singles:   one blue icon
 * - womens_singles:  one pink icon
 * - mens_doubles:   two blue icons
 * - womens_doubles:  two pink icons
 * - mixed_doubles:  one blue + one pink
 *
 * Returns null for unknown types.
 */
export const MatchTypeIcon: React.FC<MatchTypeIconProps> = ({ matchType, iconSize = 'w-5 h-5' }) => {
  const type = matchType.toLowerCase();

  let content: React.ReactNode = null;

  if (type === 'mens_singles') {
    content = <MaleIcon className={iconSize} />;
  } else if (type === 'womens_singles') {
    content = <FemaleIcon className={iconSize} />;
  } else if (type === 'mens_doubles') {
    content = (
      <span className="inline-flex items-center -space-x-2.5">
        <MaleIcon className={iconSize} />
        <MaleIcon className={iconSize} />
      </span>
    );
  } else if (type === 'womens_doubles') {
    content = (
      <span className="inline-flex items-center -space-x-2.5">
        <FemaleIcon className={iconSize} />
        <FemaleIcon className={iconSize} />
      </span>
    );
  } else if (type === 'mixed_doubles') {
    content = (
      <span className="inline-flex items-center -space-x-2.5">
        <MaleIcon className={iconSize} />
        <FemaleIcon className={iconSize} />
      </span>
    );
  }

  if (!content) return null;

  // Fixed-width wrapper so text always aligns regardless of singles vs doubles
  return (
    <span className="inline-flex items-center justify-center w-6 flex-shrink-0">
      {content}
    </span>
  );
};

export default MatchTypeIcon;
